import { useCallback, useMemo, useRef } from "react";
import _ from "lodash";
import { v4 as uuid } from "uuid";
import { debounceDelay, formCategories, presetSectionsCodeObject } from "@Helpers/constants";
import { formSectionType, TEMPLATE_SOURCE_MAIL_CREATION, TEMPLATE_SOURCE_MAIL_SIDEBAR } from "./constants";
import type { FormTemplateField, QuestionField } from "@ServerTypes/forms/forms.schema";

/**
 * The form/template being previewed, as this hook reads it. Only `template` is named; the open index
 * signature is what lets the two callers (web `formPreview`, mobile `EditableFormPreview`) hand over
 * their own wider form objects unchanged.
 */
interface PreviewFormData {
  template?: FormTemplateField[];
  [key: string]: unknown;
}

/**
 * One row of the flattened custom-field save payload `debouncedSave` builds — this is not a question
 * shape, it is the six fields that save path expects. Declared here because that payload has no
 * schema of its own on the server; reported as a gap.
 */
interface CustomFieldSaveRow {
  _id: string;
  value: unknown;
  questionType?: string;
  questionValue?: string;
  sectionId?: string;
  prevAnswer?: QuestionField["answer"];
}

/**
 * The pending template-edit buffer, keyed by the `templateEditCases` names the server's
 * `updateUsedTemplate` action consumes. Only the two keys this hook appends to are named; anything
 * else a caller keeps in there survives the spread untouched, which is what the index signature is
 * for. `changes` is `z.any()` on `updateUsedTemplateSchema`, so there is no server type to import —
 * reported as a gap.
 */
interface UpdateTemplateAfterUse {
  CONFIGURE_SECTION?: Array<{ fieldType: string; tableDataSource?: unknown[]; sectionId?: string }>;
  CONFIGURE_QUESTION?: Array<{
    questionId: string;
    configuration: { answer: { value: unknown } };
    sectionId?: string;
  }>;
  [key: string]: unknown;
}

/**
 * The question as `filterFields` reads it. `editableInForm` and `isMetadata` are persisted by the
 * form-template builder (portal-settings FormsAndFields) and drive the mailbox field split here, but
 * neither is modelled by `questionFieldSchema` — its `.catchall(z.unknown())` would type them
 * `unknown`, which no boolean position accepts. Declared here and reported as a server-schema gap,
 * the same treatment `questionFieldsHelper`'s `RenderableQuestionField` gives `requireAttachments` /
 * `fieldInstruction`. `_id` is carried along (a literal-key `Pick`, so it survives the Zod type) so
 * this stays a non-weak type that a closed caller shape can still satisfy.
 */
type FilterableQuestionField = Pick<QuestionField, "_id"> & {
  editableInForm?: boolean;
  isMetadata?: boolean;
};

interface UseFormPreviewLogicProps {
  formData: PreviewFormData;
  setFormData?: (updater: (prev: PreviewFormData) => PreviewFormData) => void;
  handleData?: (template: FormTemplateField[], sectionId: string, fieldId: string) => void;
  handleSaveCustomFields?: (fieldsData: CustomFieldSaveRow[]) => void;
  setUpdateTemplateAfterUse?: (updater: (prev: UpdateTemplateAfterUse) => UpdateTemplateAfterUse) => void;
  userDetails?: { _id?: string };
  /**
   * Accepted for parity with the callers' prop lists but never destructured — this hook builds its
   * own `debouncedSave` below. `unknown` rather than a signature, because nothing here calls it.
   */
  debouncedSave?: unknown;
  resourceType?: string;
  templateSource?: string;
}

/**
 * One row of a table section, as the answer writer below needs it: a mutable map of question `_id`
 * → captured cell. `tableDataSource` is `z.array(z.unknown())` on `formTemplateFieldSchema` (the
 * element is the row shape already modelled as `tableSectionRow` in
 * `forms/inspectionAndProgress.collection.ts`, which that schema cannot import without a cycle), so
 * the element has to be narrowed here rather than assumed.
 */
const isTableRow = (row: unknown): row is Record<string, unknown> =>
  typeof row === "object" && row !== null && !Array.isArray(row);

interface HandleCustomFieldDataParams {
  eachField: QuestionField;
  answerValue: unknown;
  fieldIndex: number;
  sectionIndex: number;
  tableRowIndex?: number;
  source?: string;
  file?: File;
}

export const useFormPreviewLogic = ({
  formData,
  setFormData,
  handleData,
  handleSaveCustomFields = () => {},
  setUpdateTemplateAfterUse,
  userDetails,
  resourceType,
  templateSource,
}: UseFormPreviewLogicProps) => {
  /**
   * Checks if a section is a preset section type that shows placeholder UI
   */
  const isPresetPlaceholderSection = useCallback((sectionCode: string): boolean => {
    return [
      presetSectionsCodeObject.SECTION_TYPE_QUANTITY,
      presetSectionsCodeObject.SECTION_TYPE_FORM,
      presetSectionsCodeObject.SECTION_TYPE_REFERENCE,
      presetSectionsCodeObject.SECTION_TYPE_WBS_ITEMS,
      presetSectionsCodeObject.SECTION_TYPE_REVIEWS,
    ].includes(sectionCode);
  }, []);

  /**
   * Checks if a section is a task section
   */
  const isTaskSection = useCallback((sectionCode: string): boolean => {
    return sectionCode === presetSectionsCodeObject.SECTION_TYPE_TASK;
  }, []);

  /**
   * Checks if a section is a milestone section
   */
  const isMilestoneSection = useCallback((sectionCode: string): boolean => {
    return sectionCode === presetSectionsCodeObject.SECTION_TYPE_MILESTONE;
  }, []);

  /**
   * Checks if a section is a table section
   */
  const isTableSection = useCallback((sectionCode: string): boolean => {
    return sectionCode === presetSectionsCodeObject.SECTION_TYPE_TABLE_SECTION;
  }, []);

  /**
   * Checks if a section is available for mobile
   */
  const isSectionAvailableForMobile = useCallback((sectionCode: string): boolean => {
    const unavailableSections = [
      presetSectionsCodeObject.SECTION_TYPE_REFERENCE,
      presetSectionsCodeObject.SECTION_TYPE_FORM,
      presetSectionsCodeObject.SECTION_TYPE_MILESTONE,
      presetSectionsCodeObject.SECTION_TYPE_WBS_ITEMS,
    ];
    return !unavailableSections.includes(sectionCode);
  }, []);

  const handleDataRef = useRef(handleData);
  const handleSaveCustomFieldsRef = useRef(handleSaveCustomFields);
  handleDataRef.current = handleData;
  handleSaveCustomFieldsRef.current = handleSaveCustomFields;

  /**
   * Handles updating field data when user inputs a value
   */
  const debouncedSave = useMemo(
    () =>
      _.debounce(
        (
          modifiedTemplate: FormTemplateField[],
          _id: string,
          sectionIndex: number,
          fieldIndex: number,
          answerValue: unknown,
          previousAns: QuestionField["answer"],
        ) => {
          if (handleDataRef.current) {
            // KNOWN BUG, preserved verbatim: a section's `_id` is `.optional()` on
            // `formTemplateFieldSchema` but `handleData` requires a `sectionId: string`, so a section
            // saved without one silently reports `undefined` as the id it edited. The index assertion
            // is `noUncheckedIndexedAccess` bookkeeping; both are erased at emit, so the call is the
            // one that runs today. The fix is to make the section `_id` required on the wire.
            handleDataRef.current(modifiedTemplate, modifiedTemplate[sectionIndex]!._id!, _id);
          }

          const fieldsData: CustomFieldSaveRow[] = [
            {
              _id,
              value: answerValue,
              questionType: modifiedTemplate[sectionIndex]!.questionFields?.[fieldIndex]?.fieldType,
              questionValue: modifiedTemplate[sectionIndex]!.questionFields?.[fieldIndex]?.questionValue,
              sectionId: modifiedTemplate[sectionIndex]!._id,
              prevAnswer: previousAns,
            },
          ];
          handleSaveCustomFieldsRef.current(fieldsData);
        },
        debounceDelay,
      ),
    [],
  );

  const filterFields = (field: FilterableQuestionField): boolean => {
    if (resourceType === formCategories.mailBox && templateSource) {
      if (templateSource === TEMPLATE_SOURCE_MAIL_CREATION) return field.editableInForm ?? true;
      if (templateSource === TEMPLATE_SOURCE_MAIL_SIDEBAR) return field.isMetadata ?? true;
      return false;
    }
    return true;
  };

  const handleCustomFieldData = ({
    eachField,
    answerValue,
    fieldIndex,
    sectionIndex,
    tableRowIndex = 0,
    source,
  }: HandleCustomFieldDataParams) => {
    const { _id } = eachField;
    const modifiedTemplate = _.cloneDeep(formData?.template);

    if (!modifiedTemplate?.[sectionIndex]) return;

    const previousAns = modifiedTemplate[sectionIndex].questionFields?.[fieldIndex]?.answer;

    if (source === formSectionType.TABLE_SECTION) {
      const modifiedTableDataSource = modifiedTemplate[sectionIndex].tableDataSource?.map(
        (row: unknown, index: number) => {
          // `formTemplateFieldSchema.tableDataSource` is `z.array(z.unknown())`, so the row arrives
          // untyped. Narrowed with `isTableRow` rather than assumed: writing a property onto a
          // non-object row would throw at runtime anyway, and the schema's own cube-qa census
          // records every one of the 277 stored elements as a plain object, so the guard never
          // rejects a real row.
          if (index === tableRowIndex && isTableRow(row)) {
            row[_id] = {
              createdDate: Date.now(),
              createdBy: userDetails?._id,
              value: answerValue,
            };
          }
          return row;
        },
      );
      modifiedTemplate[sectionIndex].tableDataSource = modifiedTableDataSource;

      if (setUpdateTemplateAfterUse) {
        setUpdateTemplateAfterUse((prev) => ({
          ...prev,
          CONFIGURE_SECTION: [
            ...(prev?.CONFIGURE_SECTION || []),
            {
              fieldType: "tableDataSource",
              tableDataSource: modifiedTableDataSource,
              sectionId: formData.template?.[sectionIndex]?._id,
            },
          ],
        }));
      }
    } else if (modifiedTemplate[sectionIndex].questionFields?.[fieldIndex]?.answer) {
      modifiedTemplate[sectionIndex].questionFields![fieldIndex].answer = {
        ...modifiedTemplate[sectionIndex].questionFields![fieldIndex].answer,
        value: answerValue,
      };
    } else if (modifiedTemplate[sectionIndex].questionFields?.[fieldIndex]) {
      modifiedTemplate[sectionIndex].questionFields![fieldIndex].answer = {
        value: answerValue,
      };
    }

    if (setFormData) {
      setFormData((prev) => ({
        ...prev,
        template: modifiedTemplate,
      }));
    }
    if (setUpdateTemplateAfterUse) {
      setUpdateTemplateAfterUse((prev) => ({
        ...prev,
        CONFIGURE_QUESTION: [
          ...(prev?.CONFIGURE_QUESTION || []).filter((q) => q.questionId != eachField._id),
          {
            questionId: eachField._id,
            configuration: { answer: { value: answerValue } },
            // Non-null: the early return above already guaranteed this index. The narrowing is
            // simply not carried into this callback; the assertion is erased at emit.
            sectionId: modifiedTemplate[sectionIndex]!._id,
          },
        ],
      }));
    }
    debouncedSave(modifiedTemplate, _id, sectionIndex, fieldIndex, answerValue, previousAns);
  };

  /**
   * Adds a new row to a table section
   */
  const handleAddTableRow = useCallback(
    ({ sectionIndex }: { sectionIndex: number }) => {
      if (!setFormData || !formData?.template?.[sectionIndex]) return;

      const newObjRow: Record<string, unknown> = {
        rowId: uuid(),
        isPreFilledRow: true,
      };

      formData.template[sectionIndex].questionFields?.forEach((field: QuestionField) => {
        newObjRow[field._id] = {};
      });

      const modifiedTemplate = [...formData.template];

      // Non-null throughout: the guard on entry already returned unless
      // `formData.template[sectionIndex]` exists, and `modifiedTemplate` is a shallow copy of that
      // same array. All assertions here are erased at emit.
      let newTableDataSource: unknown[] = [];
      if (modifiedTemplate[sectionIndex]!.tableDataSource) {
        newTableDataSource = [...modifiedTemplate[sectionIndex]!.tableDataSource!, newObjRow];
      } else {
        newTableDataSource = [newObjRow];
      }
      modifiedTemplate[sectionIndex]!.tableDataSource = newTableDataSource;

      setFormData((prev) => ({
        ...prev,
        template: modifiedTemplate,
      }));

      if (setUpdateTemplateAfterUse) {
        setUpdateTemplateAfterUse((prev) => ({
          ...prev,
          CONFIGURE_SECTION: [
            ...(prev?.CONFIGURE_SECTION || []),
            {
              fieldType: "tableDataSource",
              tableDataSource: newTableDataSource,
              sectionId: formData.template?.[sectionIndex]?._id,
            },
          ],
        }));
      }
    },
    [formData?.template, setFormData, setUpdateTemplateAfterUse],
  );

  /**
   * Deletes a row from a table section
   */
  const handleDeleteTableRow = useCallback(
    ({ sectionIndex, rowIndex }: { sectionIndex: number; rowIndex: number }) => {
      if (!setFormData || !formData?.template?.[sectionIndex]) return;

      const modifiedTemplate = [...formData.template];
      // Non-null for the same reason as in handleAddTableRow: the entry guard already returned
      // unless `formData.template[sectionIndex]` exists, and this is a shallow copy of that array.
      const dataSource = [...(modifiedTemplate[sectionIndex]!.tableDataSource || [])];

      dataSource.splice(rowIndex, 1);
      modifiedTemplate[sectionIndex]!.tableDataSource = dataSource;

      setFormData((prev) => ({
        ...prev,
        template: modifiedTemplate,
      }));

      if (setUpdateTemplateAfterUse) {
        setUpdateTemplateAfterUse((prev) => ({
          ...prev,
          CONFIGURE_SECTION: [
            ...(prev?.CONFIGURE_SECTION || []),
            {
              fieldType: "tableDataSource",
              tableDataSource: dataSource,
              sectionId: formData.template?.[sectionIndex]?._id,
            },
          ],
        }));
      }
    },
    [formData?.template, setFormData, setUpdateTemplateAfterUse],
  );

  /**
   * Determines the section type for rendering
   */
  const getSectionType = useCallback(
    (
      section: FormTemplateField | undefined,
    ): "preset-placeholder" | "task" | "milestone" | "table" | "default" | "unavailable-mobile" => {
      if (!section?.sectionCode) return "default";

      if (isPresetPlaceholderSection(section.sectionCode)) return "preset-placeholder";
      if (isTaskSection(section.sectionCode)) return "task";
      if (isMilestoneSection(section.sectionCode)) return "milestone";
      if (isTableSection(section.sectionCode)) return "table";

      return "default";
    },
    [isPresetPlaceholderSection, isTaskSection, isMilestoneSection, isTableSection],
  );

  return {
    filterFields,
    handleCustomFieldData,
    handleAddTableRow,
    handleDeleteTableRow,
    getSectionType,
    isPresetPlaceholderSection,
    isTaskSection,
    isMilestoneSection,
    isTableSection,
    isSectionAvailableForMobile,
  };
};

export default useFormPreviewLogic;
